import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ProImageSlider.css";

// =====================================================
// INSTAGRAM-STYLE IMAGE SLIDER  (Part B item 4)
//
// One image at a time, inside a fixed-aspect frame.
//
// WHAT "FITTED LIKE AN INSTAGRAM POST" MEANS HERE
//   The frame keeps a constant aspect ratio (the post's own, 4:5 by default)
//   so the page never jumps as you swipe between a portrait and a landscape
//   photo. Inside that frame the image is `object-fit: contain`:
//     • it is never stretched — the aspect ratio is always preserved;
//     • it is never cropped — `cover` would fill the frame by cutting the
//       subject's head off on a tall photo, which is exactly what the brief
//       rules out.
//   The letterbox that leaves is filled by a blurred copy of the same image,
//   the way Instagram does it, so a portrait photo in a wide frame looks
//   deliberate instead of looking like a mistake.
//
// LAZY LOADING
//   Only the current slide and its immediate neighbours get a `src` at all;
//   everything else renders an empty placeholder until you approach it. A
//   25-image Pro post therefore costs three image downloads on open, not 25,
//   and the neighbours being preloaded is what makes the next swipe smooth
//   rather than a white flash.
//
// INPUT
//   Touch swipe on mobile (with a distance threshold, so a vertical page
//   scroll is never mistaken for a swipe), arrow buttons on desktop, and the
//   left/right keyboard arrows when the slider has focus.
//
// ONE IMAGE
//   No arrows, no dots, no counter, no swipe handlers — just the image. A
//   single-image post is not a carousel.
//
// The VIDEO is deliberately not handled here. It sits BELOW the slider as its
// own element (see ProBlogCard), never as a slide inside it.
// =====================================================

// Below this many pixels a horizontal movement is a tap or the start of a
// vertical scroll, not a swipe.
const SWIPE_THRESHOLD_PX = 45;

// How many slides either side of the current one are given a real `src`.
const PRELOAD_RADIUS = 1;

function ProImageSlider({ images, alt = "", aspectRatio = "4:5" }) {
  const list = useMemo(() => (Array.isArray(images) ? images.filter(Boolean) : []), [images]);

  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // A shorter list arriving (a different post rendered into the same slot)
  // must not leave the slider pointing past the end.
  useEffect(() => {
    setIndex((current) => (current > list.length - 1 ? 0 : current));
  }, [list.length]);

  const goTo = useCallback(
    (next) => {
      if (!list.length) return;
      const clamped = Math.max(0, Math.min(list.length - 1, next));
      setIndex(clamped);
    },
    [list.length]
  );

  const prev = useCallback(() => goTo(index - 1), [goTo, index]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);

  // ── TOUCH ──
  // The vertical distance is compared with the horizontal one so that scrolling
  // the page past a slider never flips the slide.
  const onTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;

    const touch = e.changedTouches?.[0];
    if (!touch) return;

    const dx = touch.clientX - touchStartX.current;
    const dy = touch.clientY - touchStartY.current;

    touchStartX.current = null;
    touchStartY.current = null;

    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (Math.abs(dy) > Math.abs(dx)) return; // a vertical scroll

    if (dx < 0) next();
    else prev();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  if (!list.length) return null;

  // Turn "4:5" into a CSS aspect-ratio value, falling back to 4/5 on anything
  // unexpected so a malformed value can never collapse the frame to zero.
  const ratioMatch = /^(\d{1,3}):(\d{1,3})$/.exec(String(aspectRatio || ""));
  const ratio =
    ratioMatch && Number(ratioMatch[2]) > 0
      ? `${ratioMatch[1]} / ${ratioMatch[2]}`
      : "4 / 5";

  // ── SINGLE IMAGE — no controls at all ──
  if (list.length === 1) {
    return (
      <figure className="pis-frame pis-single" style={{ aspectRatio: ratio }}>
        <img className="pis-backdrop" src={list[0]} alt="" aria-hidden="true" />
        <img className="pis-image" src={list[0]} alt={alt} loading="lazy" />
      </figure>
    );
  }

  return (
    <div
      className="pis-wrap"
      role="group"
      aria-roledescription="carousel"
      aria-label={`${list.length} images`}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="pis-frame" style={{ aspectRatio: ratio }}>
        <div
          className="pis-track"
          style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}
        >
          {list.map((url, i) => {
            // Lazy: only the current slide and its neighbours are real.
            const load = Math.abs(i - index) <= PRELOAD_RADIUS;

            return (
              <div
                className="pis-slide"
                key={`${url}-${i}`}
                aria-hidden={i !== index}
                aria-label={`Image ${i + 1} of ${list.length}`}
              >
                {load ? (
                  <>
                    <img className="pis-backdrop" src={url} alt="" aria-hidden="true" />
                    <img
                      className="pis-image"
                      src={url}
                      alt={`${alt} ${i + 1}`.trim()}
                      loading={i === index ? "eager" : "lazy"}
                      decoding="async"
                    />
                  </>
                ) : (
                  <div className="pis-placeholder" />
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop arrows. Hidden at the ends rather than disabled-and-visible,
            which is how the app's other controls behave. */}
        {index > 0 && (
          <button
            type="button"
            className="pis-arrow pis-arrow-prev"
            onClick={prev}
            aria-label="Previous image"
          >
            ‹
          </button>
        )}

        {index < list.length - 1 && (
          <button
            type="button"
            className="pis-arrow pis-arrow-next"
            onClick={next}
            aria-label="Next image"
          >
            ›
          </button>
        )}

        <span className="pis-counter">
          {index + 1}/{list.length}
        </span>
      </div>

      {/* Dot indicators. Tappable, so they are navigation and not just a
          read-out — and capped so 25 images do not produce 25 dots across a
          phone screen (the counter above already gives the exact position). */}
      <div className="pis-dots" role="tablist" aria-label="Choose image">
        {list.slice(0, 10).map((url, i) => (
          <button
            key={`dot-${url}-${i}`}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Go to image ${i + 1}`}
            className={`pis-dot${i === index ? " pis-dot-active" : ""}`}
            onClick={() => goTo(i)}
          />
        ))}
        {list.length > 10 && <span className="pis-dots-more">+{list.length - 10}</span>}
      </div>
    </div>
  );
}

export default ProImageSlider;
